/**
 * KanbanView: Drag-and-drop Kanban board with 6 status columns.
 *
 * Uses @dnd-kit for drag-and-drop. Optimistic updates on drop with
 * API rollback on error. framer-motion AnimatePresence for enter/exit.
 *
 * Columns: BACKLOG | TODO | ACTIVE | BLOCKED | REVIEW | DONE
 */

import { useState, useCallback, useMemo, useRef } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Archive,
  ListTodo,
  Play,
  AlertOctagon,
  Eye,
  CheckCircle2,
} from 'lucide-react';
import type { TaskItem, TaskStatus } from '@/api/types';
import { SortableTaskCard, TaskCardInner } from './TaskCard';

// ── Column definitions ──────────────────────────────────

interface ColumnDef {
  id: TaskStatus;
  label: string;
  icon: typeof Archive;
  accentColor: string;
  accentBg: string;
}

const COLUMNS: ColumnDef[] = [
  { id: 'backlog',     label: 'Backlog',  icon: Archive,       accentColor: '#6b7280', accentBg: 'rgba(107,114,128,0.08)' },
  { id: 'todo',        label: 'Todo',     icon: ListTodo,      accentColor: '#3b82f6', accentBg: 'rgba(59,130,246,0.08)' },
  { id: 'in_progress', label: 'Active',   icon: Play,          accentColor: '#39FF14', accentBg: 'rgba(57,255,20,0.06)' },
  { id: 'blocked',     label: 'Blocked',  icon: AlertOctagon,  accentColor: '#ef4444', accentBg: 'rgba(239,68,68,0.06)' },
  { id: 'review',      label: 'Review',   icon: Eye,           accentColor: '#facc15', accentBg: 'rgba(250,204,21,0.06)' },
  { id: 'done',        label: 'Done',     icon: CheckCircle2,  accentColor: '#22c55e', accentBg: 'rgba(34,197,94,0.06)' },
];

// ── Status transition validation ────────────────────────
// Prevents illogical jumps (e.g., backlog -> done directly)
const VALID_TRANSITIONS: Record<TaskStatus, TaskStatus[]> = {
  backlog:     ['todo', 'cancelled'],
  todo:        ['backlog', 'in_progress', 'blocked', 'cancelled'],
  in_progress: ['todo', 'blocked', 'review', 'done', 'cancelled'],
  blocked:     ['todo', 'in_progress', 'cancelled'],
  review:      ['in_progress', 'done', 'blocked', 'cancelled'],
  done:        ['todo', 'in_progress'],
  cancelled:   ['todo', 'backlog'],
};

function isValidTransition(from: TaskStatus, to: TaskStatus): boolean {
  if (from === to) return false;
  return VALID_TRANSITIONS[from]?.includes(to) ?? false;
}

// ── Droppable Column ────────────────────────────────────

interface KanbanColumnProps {
  column: ColumnDef;
  tasks: TaskItem[];
  onCardClick: (task: TaskItem) => void;
  isDropTarget: boolean;
  isInvalidDrop: boolean;
  shakeTaskId: string | null;
}

function KanbanColumn({ column, tasks, onCardClick, isDropTarget, isInvalidDrop, shakeTaskId }: KanbanColumnProps) {
  const { setNodeRef } = useDroppable({ id: column.id });
  const taskIds = useMemo(() => tasks.map((t) => t.id), [tasks]);

  return (
    <div
      ref={setNodeRef}
      className={`
        flex flex-col min-w-[260px] max-w-[320px] w-full rounded-xl
        transition-all duration-150
        ${isDropTarget && !isInvalidDrop
          ? 'ring-2 ring-[var(--sp-accent)]/40 bg-[var(--sp-accent)]/[0.03]'
          : isInvalidDrop
            ? 'ring-2 ring-red-500/30 bg-red-500/[0.02]'
            : 'bg-transparent'
        }
      `}
    >
      {/* Column header */}
      <div className="flex items-center gap-2 px-3 py-2.5 mb-1">
        <column.icon
          className="w-3.5 h-3.5 shrink-0"
          style={{ color: column.accentColor }}
        />
        <span
          className="text-xs font-bold font-mono uppercase tracking-wider"
          style={{ color: column.accentColor }}
        >
          {column.label}
        </span>
        <span
          className="text-[10px] font-mono px-1.5 py-0.5 rounded-full ml-auto"
          style={{
            color: column.accentColor,
            backgroundColor: column.accentBg,
          }}
        >
          {tasks.length}
        </span>
      </div>

      {/* Card list */}
      <div className="flex-1 px-1.5 pb-2 space-y-2 overflow-y-auto min-h-[120px] max-h-[calc(100vh-220px)]">
        <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
          <AnimatePresence mode="popLayout">
            {tasks.map((task) => (
              <motion.div
                key={task.id}
                animate={shakeTaskId === task.id
                  ? { x: [0, -8, 8, -4, 4, 0] }
                  : { x: 0 }
                }
                transition={{ duration: 0.3 }}
              >
                <SortableTaskCard
                  task={task}
                  onClick={onCardClick}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </SortableContext>

        {tasks.length === 0 && (
          <div className="flex items-center justify-center h-20 text-[11px] text-gray-600 font-mono">
            No tasks
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main KanbanView ─────────────────────────────────────

interface KanbanViewProps {
  tasks: TaskItem[];
  onCardClick: (task: TaskItem) => void;
  onStatusChange: (taskId: string, newStatus: TaskStatus) => Promise<TaskItem>;
  onError: (message: string) => void;
}

export function KanbanView({ tasks, onCardClick, onStatusChange, onError }: KanbanViewProps) {
  const [activeTask, setActiveTask] = useState<TaskItem | null>(null);
  const [overColumnId, setOverColumnId] = useState<TaskStatus | null>(null);
  // Optimistic: local task list that we update before the API responds
  const [optimisticTasks, setOptimisticTasks] = useState<TaskItem[] | null>(null);
  // Shake animation on invalid drop
  const [shakeTaskId, setShakeTaskId] = useState<string | null>(null);
  const shakeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const liveTasks = optimisticTasks ?? tasks;

  // Bucket tasks by status
  const columnTasks = useMemo(() => {
    const buckets: Record<TaskStatus, TaskItem[]> = {
      backlog: [],
      todo: [],
      in_progress: [],
      blocked: [],
      review: [],
      done: [],
      cancelled: [],
    };
    for (const task of liveTasks) {
      const bucket = buckets[task.status];
      if (bucket) bucket.push(task);
    }
    return buckets;
  }, [liveTasks]);

  // Sensors: pointer with 5px activation distance (to distinguish click from drag)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  // Is the current drag target a valid transition?
  const isInvalidDrop = useMemo(() => {
    if (!activeTask || !overColumnId) return false;
    return !isValidTransition(activeTask.status, overColumnId);
  }, [activeTask, overColumnId]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const task = event.active.data.current?.task as TaskItem | undefined;
    if (task) {
      setActiveTask(task);
      setOptimisticTasks(null); // Clear any stale optimistic state
    }
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const overId = event.over?.id as TaskStatus | undefined;
    if (overId && COLUMNS.some((c) => c.id === overId)) {
      setOverColumnId(overId);
    } else {
      setOverColumnId(null);
    }
  }, []);

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    const task = activeTask;
    const targetColumnId = event.over?.id as TaskStatus | undefined;

    setActiveTask(null);
    setOverColumnId(null);

    if (!task || !targetColumnId) return;
    if (task.status === targetColumnId) return;
    if (!isValidTransition(task.status, targetColumnId)) {
      // Shake animation on invalid drop
      setShakeTaskId(task.id);
      if (shakeTimerRef.current) clearTimeout(shakeTimerRef.current);
      shakeTimerRef.current = setTimeout(() => setShakeTaskId(null), 400);
      onError(`Cannot move from ${task.status} to ${targetColumnId}`);
      return;
    }

    // Optimistic update: move card to target column immediately
    const optimistic = tasks.map((t) =>
      t.id === task.id ? { ...t, status: targetColumnId } : t,
    );
    setOptimisticTasks(optimistic);

    try {
      await onStatusChange(task.id, targetColumnId);
      // Success: parent will refresh tasks, clear optimistic state
      setOptimisticTasks(null);
    } catch {
      // Roll back: restore original state
      setOptimisticTasks(null);
      onError(`Failed to move task to ${targetColumnId}. Rolled back.`);
    }
  }, [activeTask, tasks, onStatusChange, onError]);

  const handleDragCancel = useCallback(() => {
    setActiveTask(null);
    setOverColumnId(null);
    setOptimisticTasks(null);
  }, []);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="flex gap-3 px-4 pb-4 overflow-x-auto">
        {COLUMNS.map((column) => (
          <KanbanColumn
            key={column.id}
            column={column}
            tasks={columnTasks[column.id] ?? []}
            onCardClick={onCardClick}
            isDropTarget={overColumnId === column.id}
            isInvalidDrop={overColumnId === column.id && isInvalidDrop}
            shakeTaskId={shakeTaskId}
          />
        ))}
      </div>

      {/* Drag overlay — renders the card being dragged */}
      <DragOverlay dropAnimation={null}>
        {activeTask && (
          <div className="w-[280px]">
            <TaskCardInner task={activeTask} isDragging isOverlay />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
